import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader2,
  CircleDashed,
  ChevronDown,
  ListChecks,
} from 'lucide-react';

import { useTaskPlan } from '../../context/TaskPlanProvider';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'in progress':
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />;
    case 'done':
      return <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />;
    case 'cancelled':
      return <XCircle className="h-3.5 w-3.5 text-red-400" />;
    case 'todo':
    default:
      return <CircleDashed className="h-3.5 w-3.5 text-zinc-600" />;
  }
};

const InlineGoalItem = ({ goal, isExpanded, onToggleClick }) => {
  const doneCount = goal.actions.filter((a) => a.status === 'done').length;
  const totalCount = goal.actions.length;
  const progress = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  return (
    <div>
      <button
        className="flex w-full items-center gap-3 px-3 py-2 transition-colors duration-150 hover:bg-zinc-800/40"
        onClick={() => onToggleClick(goal.goal_id)}
      >
        <StatusIcon status={goal.status} />
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-[11px] font-medium text-zinc-300">
            {goal.description}
          </p>
          {totalCount > 0 && (
            <div className="mt-1 flex items-center gap-2">
              <div className="h-1 flex-1 rounded-full bg-zinc-800">
                <div
                  className="h-1 rounded-full bg-blue-400 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[10px] text-zinc-500">
                {doneCount}/{totalCount}
              </span>
            </div>
          )}
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="bg-black/10 pb-1 pl-8 pr-3 pt-0.5">
            {goal.actions.map((action) => (
              <div
                key={action.action_id}
                className="flex items-center gap-2 py-1.5"
              >
                <StatusIcon status={action.status} />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-zinc-400">
                    {action.description}
                  </p>
                  {action.type === 'tool_call' && action.tool_name && (
                    <span className="mt-0.5 inline-block rounded bg-zinc-800 px-1 py-px font-mono text-[9px] text-zinc-500">
                      {action.tool_name}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const InlineTaskPlan = () => {
  const { plan, planStatus } = useTaskPlan();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);

  const toggleGoal = (goalId) => {
    setExpandedGoalId((prevId) => (prevId === goalId ? null : goalId));
  };

  if (!plan || plan.length === 0) return null;

  const isPlanning = planStatus === 'planning';
  const totalActions = plan.reduce((acc, g) => acc + g.actions.length, 0);
  const doneActions = plan.reduce(
    (acc, g) => acc + g.actions.filter((a) => a.status === 'done').length,
    0,
  );

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50">
      <button
        className="flex w-full items-center justify-between px-4 py-2.5 transition-colors hover:bg-zinc-800/30"
        onClick={() => setCollapsed((p) => !p)}
      >
        <div className="flex items-center gap-2">
          <ListChecks
            className={`h-4 w-4 ${isPlanning ? 'animate-pulse text-blue-400' : 'text-zinc-400'}`}
          />
          <span className="text-xs font-medium text-zinc-300">
            {isPlanning ? 'Planning...' : `Execution plan`}
          </span>
          {totalActions > 0 && (
            <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-500">
              {doneActions}/{totalActions} done
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-zinc-500 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
        />
      </button>

      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: collapsed ? '0fr' : '1fr' }}
      >
        <div className="overflow-hidden">
          <div className="divide-y divide-zinc-800/30">
            {plan.map((goal) => (
              <InlineGoalItem
                key={goal.goal_id}
                goal={goal}
                isExpanded={expandedGoalId === goal.goal_id}
                onToggleClick={toggleGoal}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineTaskPlan;
